<script>
import { Form as VeeForm } from 'vee-validate'
import { h, defineComponent, watch } from 'vue'

function createMetaSnapshot(meta) {
  return {
    valid: !!meta?.valid,
    invalid: !meta?.valid,
    dirty: !!meta?.dirty,
    touched: !!meta?.touched,
    pending: !!meta?.pending,
  }
}

function createErrorsSnapshot(errors) {
  return { ...(errors || {}) }
}

const FormStateSync = defineComponent({
  name: 'FormStateSync',
  props: {
    meta: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
  },
  emits: ['update:meta', 'update:errors'],
  setup(props, { emit, slots }) {
    watch(
      () => createMetaSnapshot(props.meta),
      (val) => {
        emit('update:meta', val)
      },
      { immediate: true, flush: 'post' }
    )

    watch(
      () => createErrorsSnapshot(props.errors),
      (val) => {
        emit('update:errors', val)
      },
      { immediate: true, flush: 'post' }
    )

    return () => (slots.default ? slots.default() : null)
  },
})

export default defineComponent({
  name: 'VeeFormWrapper',
  components: { VeeForm, FormStateSync },
  inheritAttrs: false,
  props: {
    validationSchema: { type: [Object, null], default: null },
    validateOnMount: { type: Boolean, default: false },
    initialValues: { type: Object, default: () => ({}) },
    as: { type: String, default: 'form' },
    novalidate: { type: Boolean, default: false },
  },
  emits: ['submit', 'invalid-submit', 'update:meta', 'update:errors'],
  methods: {
    onSubmit(values, actions) {
      this.$emit('submit', values, actions)
    },
    onInvalidSubmit(payload) {
      this.$emit('invalid-submit', payload)
    },
    onMetaUpdate(meta) {
      this.$emit('update:meta', meta)
    },
    onErrorsUpdate(errors) {
      this.$emit('update:errors', errors)
    },
    async validate() {
      const formRef = this.$refs.veeForm
      if (formRef) {
        return formRef.validate()
      }
      return { valid: false, errors: {} }
    },
    resetForm(state) {
      const formRef = this.$refs.veeForm
      if (formRef) {
        formRef.resetForm(state)
      }
    },
    setFieldError(field, message) {
      const formRef = this.$refs.veeForm
      if (formRef) {
        formRef.setFieldError(field, message)
      }
    },
    setFieldValue(field, value, shouldValidate = true) {
      const formRef = this.$refs.veeForm
      if (formRef) {
        formRef.setFieldValue(field, value, shouldValidate)
      }
    },
    getFieldValue(field) {
      const formRef = this.$refs.veeForm
      if (formRef && formRef.values) {
        return formRef.values[field]
      }
      return undefined
    },
    getFieldError(field) {
      const formRef = this.$refs.veeForm
      if (formRef && formRef.errors) {
        return formRef.errors[field]
      }
      return undefined
    },
    setErrors(errors) {
      const formRef = this.$refs.veeForm
      if (formRef) {
        formRef.setErrors(errors)
      }
    },
  },
  expose: ['validate', 'resetForm', 'setFieldError', 'setFieldValue', 'setErrors', 'getFieldValue', 'getFieldError'],
  render() {
    const self = this
    const formProps = {
      ref: 'veeForm',
      as: this.as,
      novalidate: this.novalidate,
      validationSchema: this.validationSchema,
      validateOnMount: this.validateOnMount,
      initialValues: this.initialValues,
      onSubmit: this.onSubmit,
      onInvalidSubmit: this.onInvalidSubmit,
    }

    Object.assign(formProps, this.$attrs)

    return h(VeeForm, formProps, {
      default: (slotProps) => {
        return h(
          FormStateSync,
          {
            meta: slotProps.meta,
            errors: slotProps.errors,
            'onUpdate:meta': self.onMetaUpdate,
            'onUpdate:errors': self.onErrorsUpdate,
          },
          {
            default: () => (self.$slots.default ? self.$slots.default(slotProps) : null),
          }
        )
      },
    })
  },
})
</script>
